/**
 * Contexte des rappels actifs (RDV + tâches).
 * Alimenté par ReminderSystem, consommé par la notification du tableau de bord.
 */

import React, { createContext, useCallback, useContext, useState } from 'react';

export interface ReminderItem {
  id: string;
  title: string;
  description: string | null;
  appointment_datetime: string | null;
  reminder_datetime: string;
  status: string;
  item_type: 'appointment' | 'task';
  invoice_id?: string | null;
}

interface ReminderContextValue {
  activeReminders: ReminderItem[];
  setActiveReminders: React.Dispatch<React.SetStateAction<ReminderItem[]>>;
  refreshReminders: () => Promise<void>;
  registerRefresh: (fn: () => Promise<void>) => void;
}

const ReminderContext = createContext<ReminderContextValue | null>(null);

export function ReminderProvider({ children }: { children: React.ReactNode }) {
  const [activeReminders, setActiveReminders] = useState<ReminderItem[]>([]);
  const refreshRef = React.useRef<(() => Promise<void>) | null>(null);
  const registerRefresh = useCallback((fn: () => Promise<void>) => {
    refreshRef.current = fn;
  }, []);
  const refreshReminders = useCallback(async () => {
    if (refreshRef.current) await refreshRef.current();
  }, []);

  const value: ReminderContextValue = {
    activeReminders,
    setActiveReminders,
    refreshReminders,
    registerRefresh,
  };

  return <ReminderContext.Provider value={value}>{children}</ReminderContext.Provider>;
}

export function useReminderContext() {
  const ctx = useContext(ReminderContext);
  if (!ctx)
    return {
      activeReminders: [] as ReminderItem[],
      setActiveReminders: () => {},
      refreshReminders: async () => {},
      registerRefresh: () => {},
    };
  return ctx;
}
